import { useMemo, useState } from 'react'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { cn } from 'components/UI/cn'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import { WizardHeader, WizardFooter } from 'components/Mobile/wizard'
import { Alert } from 'components/FbUI/Alert'
import { useUserOptions } from 'services/users'
import { FbLink } from 'components/FbUI/FbLink'
import useApiService from 'services/api'
import {
  type ReminderCreateUpdateRequest,
  useCreateReminder,
} from 'services/reminders'
import { ReminderCreatePayload, ReminderType } from 'models/reminders'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import { DatePicker } from 'components/FormUtils/DatePicker'
import { useAuth } from 'context/authentication/useAuth'
import { useMountEffect } from 'hooks/useMountEffect'
import SearchPage from '../mobile/search'

const QUICK_VALUES = ['1_week', '2_weeks', '1_month', '3_months'] as const
type QuickSchedule = (typeof QUICK_VALUES)[number]

const QUICK_LABEL: Record<QuickSchedule, string> = {
  '1_week': '1 week',
  '2_weeks': '2 weeks',
  '1_month': '1 month',
  '3_months': '3 months',
}

// Reminder-first wizard steps (FIR-7784): add the reminder, then optionally
// scope it to a company. Mirrors the note-first create flow.
type ReminderStep = 'reminder' | 'company'
const STEPS: readonly ReminderStep[] = ['reminder', 'company']
const STEP_TITLE: Record<ReminderStep, string> = {
  reminder: 'Add a reminder',
  company: 'Choose a company',
}

const reminderSchema = z
  .object({
    title: z
      .string()
      .max(50, 'Title must be 50 characters or less')
      .refine((v) => !!v, { message: 'Title is required' }),
    run_at_date: z.string().optional(),
    run_at_time: z.string().optional(),
    quick_schedule: z
      .union([
        z.literal('1_week'),
        z.literal('2_weeks'),
        z.literal('1_month'),
        z.literal('3_months'),
      ])
      .optional()
      .nullable(),
    quick_choice: z
      .union([
        z.literal('1_week'),
        z.literal('2_weeks'),
        z.literal('1_month'),
        z.literal('3_months'),
      ])
      .optional()
      .nullable(),
    frequency: z
      .union([
        z.literal('one_off'),
        z.literal('daily'),
        z.literal('weekly'),
        z.literal('monthly'),
      ])
      .default('one_off'),
    recipients: z
      .array(z.number())
      .min(1, 'You must select at least one recipient'),
    content: z.string().optional().nullable(),
  })
  .refine(
    (v) => {
      // Either quick_schedule OR a date must be provided
      return !!v.quick_schedule || !!v.run_at_date
    },
    {
      message: 'Select a date or use a quick schedule option',
      path: ['run_at_date'],
    }
  )

type ReminderFormValues = z.infer<typeof reminderSchema>

function mapFrequencyToType(
  freq: ReminderFormValues['frequency']
): ReminderType {
  switch (freq) {
    case 'daily':
      return 'daily'
    case 'weekly':
      return 'weekly'
    case 'monthly':
      return 'monthly'
    case 'one_off':
    default:
      return 'one_off'
  }
}

export function MobileCreateReminderPage() {
  const api = useApiService()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()

  // Optional deep-link: arrive with a company already in mind (parity with the
  // Notes create flow). It pre-resolves the company for the company step; the
  // flow still starts on the reminder step.
  const preselectedChainId = Number(searchParams.get('chain_id')) || undefined

  const [stepIndex, setStepIndex] = useState(0)
  const step = STEPS[stepIndex]
  const stepNum = stepIndex + 1
  const stepCount = STEPS.length

  // Company scoping is optional (FIR-7665 / FIR-7784) — the reminder can be
  // saved without one, matching desktop (`ReminderModal` sends
  // `contact_company || null`).
  const [selectedSearchResult, setSelectedSearchResult] = useState<
    ContactCompanySearchResult | DoorSearchResult | undefined
  >()

  const isDoorSelected =
    !!selectedSearchResult && 'chain_proxy_id' in selectedSearchResult

  // Resolve the contact-company / door-proxy ids from the picked result
  // (chain → useGetChain, door → useGetPlaceV2), same as MobileCreateNotePage.
  const chainId = !isDoorSelected
    ? Number(selectedSearchResult?.item_id) || preselectedChainId
    : undefined
  const { data: chainData } = api.useGetChain(chainId)

  const restaurantId = isDoorSelected
    ? String((selectedSearchResult as DoorSearchResult).id)
    : undefined
  const { data: doorProxy } = api.useGetPlaceV2(restaurantId)

  const contactCompanyId = isDoorSelected
    ? doorProxy?.chain_proxy?.id
    : chainData?.contact_company_id
  const doorProxyId = isDoorSelected ? doorProxy?.id : undefined

  const companyDisplay = isDoorSelected
    ? doorProxy?.chain_proxy?.name || ''
    : selectedSearchResult?.name || chainData?.chain || ''
  const doorDisplay = isDoorSelected
    ? (selectedSearchResult as DoorSearchResult)?.name ||
      doorProxy?.restaurant?.restaurant_name ||
      ''
    : ''

  const methods = useForm<ReminderFormValues>({
    resolver: zodResolver(reminderSchema),
    mode: 'onChange',
    defaultValues: {
      title: '',
      run_at_date: '',
      run_at_time: '09:00',
      quick_schedule: null,
      quick_choice: null,
      frequency: 'one_off',
      recipients: user?.id ? [user.id] : [],
      content: '',
    },
  })

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isSubmitting, isValid, errors },
  } = methods

  const quickSchedule = watch('quick_schedule')
  const quickChoice = watch('quick_choice')
  const runAtDate = watch('run_at_date')
  const runAtTime = watch('run_at_time') || '09:00'
  const recipients = watch('recipients')

  useMountEffect(() => () => reset())

  const runAt = useMemo(() => {
    if (!runAtDate) return undefined
    return new Date(`${runAtDate}T${runAtTime}:00`)
  }, [runAtDate, runAtTime])

  const dateTimeError = useMemo(() => {
    if (!runAtDate || quickSchedule) return null
    const selected = new Date(`${runAtDate}T${runAtTime}:00`)
    const now = new Date()
    if (selected <= now) {
      return 'Please select a future date and time for the reminder'
    }
    return null
  }, [runAtDate, runAtTime, quickSchedule])

  // Users for recipient select
  const { data: userOptions } = useUserOptions({ onlyActive: true })
  const recipientOptions =
    userOptions?.map((u: any) => ({
      label: u.label,
      value: u.value,
    })) ?? []

  const frequencyOptions = [
    { label: 'Once', value: 'one_off' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
  ]

  const createReminderMutation = useCreateReminder()

  async function submitReminder(values: ReminderFormValues) {
    let run_at: string | undefined
    if (values.quick_schedule) {
      // backend handles quick_schedule + no run_at
    } else if (runAt) {
      run_at = runAt.toISOString()
    }

    const payload: ReminderCreatePayload = {
      title: values.title,
      content: values.content || '',
      recipients: values.recipients,
      contact_company: contactCompanyId,
      door_proxy: doorProxyId,
      run_at,
      quick_schedule: values.quick_schedule ?? undefined,
      reminder_type: mapFrequencyToType(values.frequency),
    }

    try {
      await createReminderMutation.mutateAsync({
        data: payload as unknown as ReminderCreateUpdateRequest,
      })
      toast.success('Reminder scheduled')
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      navigate(-1)
    } catch {
      toast.error('Failed to schedule reminder')
    }
  }

  const onSubmit = handleSubmit(async (values) => {
    await submitReminder(values)
  })

  const handleQuickScheduleClick = (value: QuickSchedule) => {
    const today = new Date()

    const target = new Date(today)
    switch (value) {
      case '1_week':
        target.setDate(today.getDate() + 7)
        break
      case '2_weeks':
        target.setDate(today.getDate() + 14)
        break
      case '1_month':
        target.setMonth(today.getMonth() + 1)
        break
      case '3_months':
        target.setMonth(today.getMonth() + 3)
        break
    }

    const year = target.getFullYear()
    const month = String(target.getMonth() + 1).padStart(2, '0')
    const day = String(target.getDate()).padStart(2, '0')
    const formatted = `${year}-${month}-${day}`

    // set both actual date and quick_schedule, and mark active for styling
    setValue('run_at_date', formatted, { shouldValidate: true })
    setValue('quick_schedule', value, { shouldValidate: true })
    setValue('quick_choice', value, { shouldValidate: false })
  }

  // The reminder details (step 1) must be valid before advancing to the
  // company step or submitting.
  const reminderIncomplete =
    !isValid || !!dateTimeError || recipients.length === 0

  const goBack = () => setStepIndex(0)

  const handleNextFromReminder = () => {
    if (!reminderIncomplete) setStepIndex(1)
  }

  const renderFooter = () => {
    switch (step) {
      case 'reminder':
        return (
          <WizardFooter.Root>
            <WizardFooter.Next
              onClick={handleNextFromReminder}
              disabled={reminderIncomplete}
            >
              Next
            </WizardFooter.Next>
          </WizardFooter.Root>
        )
      case 'company':
        return (
          <WizardFooter.Root>
            <WizardFooter.Back onClick={goBack} disabled={isSubmitting} />
            <WizardFooter.Next
              onClick={onSubmit}
              disabled={
                isSubmitting ||
                reminderIncomplete ||
                // A preselected chain must resolve its company before submit,
                // otherwise the reminder would be created unscoped (FIR-7665).
                (!!preselectedChainId && !contactCompanyId)
              }
            >
              {isSubmitting ? 'Scheduling...' : 'Schedule Reminder'}
            </WizardFooter.Next>
          </WizardFooter.Root>
        )
    }
  }

  const renderBody = () => {
    switch (step) {
      case 'company':
        return (
          <div className="w-full">
            <SearchPage
              hideBack
              onClick={(result) => setSelectedSearchResult(result)}
              selectedItemId={
                selectedSearchResult?.item_id ?? preselectedChainId
              }
            />
          </div>
        )
      case 'reminder':
        return (
          <FormProvider {...methods}>
            <div className="flex flex-col gap-4 px-4 pb-4 pt-2">
              {/* Title */}
              <div>
                <label className="text-base font-medium text-gray-800 mb-1">
                  Title*
                </label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm
                           placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600"
                  placeholder="Reminder title"
                  {...register('title')}
                />
                {errors.title && (
                  <p className="mt-1 text-xs text-error-strong">
                    {String(errors.title.message)}
                  </p>
                )}
              </div>
              {/* Date + quick schedule using DatePicker */}
              <div>
                <label className="text-base font-medium text-gray-800 mb-1">
                  Date*
                </label>
                <Controller
                  control={control}
                  name="run_at_date"
                  render={({ field }) => (
                    <DatePicker
                      value={field.value}
                      onChange={(val) => {
                        field.onChange(val)
                        // manual date clears quick flags
                        setValue('quick_schedule', null, {
                          shouldValidate: true,
                        })
                        setValue('quick_choice', null, {
                          shouldValidate: false,
                        })
                      }}
                      className="w-full"
                    />
                  )}
                />
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {QUICK_VALUES.map((key) => {
                    const isActive = quickChoice === key
                    return (
                      <button
                        key={key}
                        type="button"
                        className={cn(
                          'px-2 py-1.5 text-xs border rounded w-full',
                          isActive
                            ? 'border-brand-active bg-basil-50 text-brand-active'
                            : 'border-line bg-surface text-fg-secondary'
                        )}
                        onClick={() => handleQuickScheduleClick(key)}
                      >
                        {QUICK_LABEL[key]}
                      </button>
                    )
                  })}
                </div>
                {errors.run_at_date && !dateTimeError && (
                  <p className="mt-1 text-xs text-error-strong">
                    {String(errors.run_at_date.message)}
                  </p>
                )}
              </div>
              {/* Time (optional) */}
              <div>
                <label
                  htmlFor="run_at_time"
                  className="text-base font-medium text-gray-800 mb-1"
                >
                  Time{' '}
                  <span className="italic text-sm font-normal">- optional</span>
                </label>
                <Controller
                  control={control}
                  name="run_at_time"
                  render={({ field }) => (
                    <input
                      id="run_at_time"
                      type="time"
                      aria-label="Time"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm
                               placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
              </div>
              {/* Frequency */}
              <div>
                <label className="text-base font-medium text-gray-800 mb-1">
                  Frequency
                </label>
                <Controller
                  control={control}
                  name="frequency"
                  render={({ field }) => (
                    <ReactSelectWrapper
                      placeholder="Select frequency"
                      options={frequencyOptions}
                      value={
                        frequencyOptions.find((o) => o.value === field.value) ||
                        null
                      }
                      onChange={(opt: any) => field.onChange(opt?.value)}
                      isClearable={false}
                    />
                  )}
                />
              </div>
              {/* Recipients (multi select) */}
              <div>
                <label className="text-base font-medium text-gray-800 mb-1">
                  Recipient(s)*
                </label>
                <Controller
                  control={control}
                  name="recipients"
                  render={({ field }) => (
                    <ReactSelectWrapper
                      isMulti
                      placeholder="Select recipients..."
                      value={recipientOptions.filter((u: any) =>
                        field.value?.includes(u.value)
                      )}
                      options={recipientOptions as any}
                      onChange={(v) =>
                        field.onChange(
                          (v as any[]).map((i) => (i as any).value)
                        )
                      }
                    />
                  )}
                />
                {errors.recipients && (
                  <p className="mt-1 text-xs text-error-strong">
                    {String(errors.recipients.message)}
                  </p>
                )}
              </div>
              {/* Content */}
              <div>
                <label className="text-base font-medium text-gray-800 mb-1">
                  Content
                  <span className="italic text-sm font-normal">
                    {' '}
                    - optional
                  </span>
                </label>
                <textarea
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm
                           placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600"
                  placeholder="Reminder details..."
                  {...register('content')}
                />
              </div>
              {/* Date/time validation error */}
              {dateTimeError && (
                <Alert color="danger" description={dateTimeError} />
              )}
            </div>
          </FormProvider>
        )
    }
  }

  return (
    <MobileModal
      fullScreen
      onCancel={() => navigate(-1)}
      header={
        <WizardHeader
          eyebrow="Create a reminder"
          title={STEP_TITLE[step]}
          stepNum={stepNum}
          stepCount={stepCount}
        >
          {step === 'company' &&
            (contactCompanyId ? (
              <div className="mt-1 flex items-center gap-2 text-sm">
                <FbLink to={`/chain/${chainId || contactCompanyId}`}>
                  {companyDisplay}
                </FbLink>
                {isDoorSelected && doorProxyId && (
                  <>
                    <span className="text-fg-tertiary">/</span>
                    <FbLink to={`/restaurant/${doorProxyId}`}>
                      {doorDisplay}
                    </FbLink>
                  </>
                )}
              </div>
            ) : (
              <div className="text-xs text-fg-tertiary mt-2">
                Optionally scope this reminder to a company — or save without
                one.
              </div>
            ))}
        </WizardHeader>
      }
      footer={renderFooter()}
    >
      {renderBody()}
    </MobileModal>
  )
}
